import React from "react";
import SectionHeader from "@/components/SectionHeader";
import MetricRow from "@/components/MetricRow";
import WorkCard from "@/components/WorkCard";

const TV_EPISODES = [
  {
    index: "01",
    tag: "ET Now · Episode",
    title: "Balanced Advantage Funds vs Aggressive Hybrid Funds.",
    caption: "When markets are uncertain, investors panic. We didn't lecture. We talked about the two defensive categories that were trending. Result: 36,000+ views on YouTube.",
    image: "01 Broadcast and thought leadership 1.jpg",
    link: "https://youtu.be/HUrjoDiyclA",
  },
  {
    index: "02",
    tag: "ET Now · Episode",
    title: "Financial Planning in Your 40s.",
    caption: "Your 40s are make-or-break for wealth building. The episode tackled the milestones, mistakes, and strategies that define whether your 50s and 60s are secure or stressed. Result: 31,000+ views.",
    image: "02 Broadcast and thought leadership 2.jpg",
    link: "https://youtu.be/rH4EAy5e5tg",
  },
];

const PODCASTS = [
  {
    index: "03",
    tag: "Beyond Returns · Dr M Pattabiraman, IIT Madras",
    title: "The Big Risk Facing Parag Parikh Flexi Cap Investors.",
    caption: "An entire episode on investing, using India's most popular fund as the hook.",
    image: "03 Podcast 1.jpg",
    link: "https://youtu.be/t3QvdsdUgAU",
  },
  {
    index: "04",
    tag: "Beyond Returns · Ritesh Jain, Pinetree Macro",
    title: "You Won't Be Able To Afford Gold.",
    caption: "An episode on macroeconomy using gold as the hook.",
    image: "04 Podcast 2.jpg",
    link: "https://youtu.be/gTNKcm02qGA",
  },
];

export default function BroadcastPage() {
  return (
    <>
      <SectionHeader
        eyebrow="Case Study 04"
        pageIndex="04"
        title="Broadcast & Thought Leadership"
        kicker="Become the showrunner, not just the sponsor."
        lede="Transformed a sponsored television slot into a thought leadership platform that established ET Money's co-founder as a leading voice in wealth tech, while maximising RoI from a nationally televised broadcast through omnichannel repurposing."
      />

      {/* Metrics */}
      <section className="mt-20">
        <MetricRow
          items={[
            { value: "1×", label: "National TV reach" },
            { value: "30,000+", label: "Top episode views" },
            { value: "10,000+", label: "Average views per episode" },
            { value: "4", label: "Distribution channels" },
          ]}
        />
      </section>

      {/* Part A */}
      <section className="mx-auto max-w-[1440px] px-6 pt-28 lg:px-12">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4A574]">Part A</p>
        <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">
          The ET Money Show <span className="italic text-[#D4A574]">on ET Now</span>.
        </h2>
        <p className="mt-6 max-w-[64ch] text-[15px] leading-relaxed text-[#EDE4D3]/70">
          I owned the entire production lifecycle, identifying market topics that resonate with both TV and social-native audiences, scripting Q&amp;A flows that wove ET Money&rsquo;s philosophy into every exchange, and treating each 17-minute segment as raw material for an omnichannel content engine.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-x-8 gap-y-14 md:grid-cols-2">
          {TV_EPISODES.map((e) => (
            <WorkCard key={e.index} {...e} ratio="16/9" />
          ))}
        </div>
      </section>

      {/* Part B */}
      <section className="mx-auto max-w-[1440px] px-6 pt-28 lg:px-12">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4A574]">Part B</p>
        <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">
          Beyond Returns <span className="italic text-[#D4A574]">Podcast</span>.
        </h2>
        <p className="mt-6 max-w-[64ch] text-[15px] leading-relaxed text-[#EDE4D3]/70">
          A podcast property that moves beyond returns, literally. Wealth is about life goals, not just portfolio performance. I conceptualised the format, curated guests, hosted episodes, and obsessed over post-production. Community engagement became our growth lever.
        </p>

        <div className="mt-12 grid grid-cols-1 gap-x-8 gap-y-14 md:grid-cols-2">
          {PODCASTS.map((e) => (
            <WorkCard key={e.index} {...e} ratio="16/9" />
          ))}
        </div>
      </section>

      {/* Comparison */}
      <section className="mx-auto max-w-[1200px] px-6 pt-32 lg:px-12">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#D4A574]">The Multiplier</p>
        <h2 className="mt-4 font-serif text-3xl tracking-tight lg:text-4xl">
          Why broadcast + podcast <span className="italic text-[#D4A574]">&gt; social alone</span>.
        </h2>

        <div className="mt-10 overflow-hidden border border-[#EDE4D3]/12">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-[#11151F] text-[10px] uppercase tracking-[0.22em] text-[#EDE4D3]/55">
                <th className="px-6 py-4 text-left font-medium">Channel</th>
                <th className="px-6 py-4 text-left font-medium">Trust</th>
                <th className="px-6 py-4 text-left font-medium">Depth</th>
                <th className="px-6 py-4 text-left font-medium">Reach</th>
                <th className="px-6 py-4 text-left font-medium">Authority</th>
              </tr>
            </thead>
            <tbody className="text-[#EDE4D3]/80">
              {[
                ["Social Media", "Medium", "60 sec", "Wide, shallow", "Viral, not authoritative"],
                ["Broadcast TV", "High", "15 min", "Narrow, high-income", "Institutional"],
                ["Podcast", "High", "40 min", "Loyal, engaged", "Expert-level"],
              ].map((row, i) => (
                <tr key={i} className="border-t border-[#EDE4D3]/8">
                  {row.map((c, j) => (
                    <td key={j} className="px-6 py-4">{c}</td>
                  ))}
                </tr>
              ))}
              <tr className="border-t border-[#EDE4D3]/12 bg-[#D4A574]/8 font-serif text-[#D4A574]">
                <td className="px-6 py-5">Combined</td>
                <td className="px-6 py-5">Maximum</td>
                <td className="px-6 py-5">Multi-format</td>
                <td className="px-6 py-5">Multiplied</td>
                <td className="px-6 py-5 italic">Thought Leader</td>
              </tr>
            </tbody>
          </table>
        </div>

        <p className="mt-10 max-w-[60ch] text-[15px] italic leading-relaxed text-[#EDE4D3]/70">
          The playbook: create once, distribute everywhere. More importantly, use long-form broadcast to establish an unshakeable authority that social content can&rsquo;t touch.
        </p>
      </section>
    </>
  );
}
